import { useEffect, useRef, useState } from 'react';
import { faqs, faqCategories } from '../../../mocks/faq';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

interface AccordionItemProps {
  question: string;
  answer: string;
  category: string;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
  inView: boolean;
}

function AccordionItem({ question, answer, category, isOpen, onToggle, index, inView }: AccordionItemProps) {
  const categoryColors: Record<string, string> = {
    Spolupráca: 'text-orange-500 bg-orange-50 border-orange-100',
    Ceny: 'text-emerald-600 bg-emerald-50 border-emerald-100',
    Výsledky: 'text-amber-600 bg-amber-50 border-amber-100',
    Proces: 'text-violet-600 bg-violet-50 border-violet-100',
    Reporting: 'text-sky-600 bg-sky-50 border-sky-100',
    Automatizácie: 'text-rose-600 bg-rose-50 border-rose-100',
  };
  const tagStyle = categoryColors[category] ?? 'text-zinc-600 bg-zinc-100 border-zinc-200';

  return (
    <div
      className={`border rounded-2xl overflow-hidden transition-all duration-500 cursor-pointer
        ${isOpen ? 'border-orange-200 bg-orange-50/40' : 'border-zinc-100 bg-white hover:border-zinc-200'}
        ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      style={{ transitionDelay: `${150 + index * 60}ms` }}
      onClick={onToggle}
    >
      {/* Header */}
      <div className="flex items-start gap-4 p-5 md:p-6">
        <div
          className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 mt-0.5 transition-all duration-300
            ${isOpen ? 'bg-orange-500' : 'bg-zinc-100'}`}
        >
          <i className={`ri-question-line text-sm transition-colors duration-300 ${isOpen ? 'text-white' : 'text-zinc-500'}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className={`inline-flex items-center border text-xs font-semibold px-2.5 py-0.5 rounded-full mb-2 ${tagStyle}`}>
                {category}
              </span>
              <h4 className={`text-base font-bold leading-snug transition-colors duration-200 ${isOpen ? 'text-orange-600' : 'text-zinc-900'}`}>
                {question}
              </h4>
            </div>
            <div
              className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5 transition-all duration-300
                ${isOpen ? 'bg-orange-500 rotate-45' : 'bg-zinc-100 rotate-0'}`}
            >
              <i className={`ri-add-line text-sm transition-colors duration-300 ${isOpen ? 'text-white' : 'text-zinc-500'}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Answer */}
      <div
        className={`overflow-hidden transition-all duration-400 ease-in-out ${isOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="px-5 md:px-6 pb-5 md:pb-6 pt-0 pl-[72px] md:pl-[88px]">
          <p className="text-zinc-600 text-sm leading-relaxed">{answer}</p>
        </div>
      </div>
    </div>
  );
}

export default function FaqSection() {
  const { ref, inView } = useInView();
  const [activeCategory, setActiveCategory] = useState('Všetky');
  const [openId, setOpenId] = useState<number | null>(1);

  const filtered = activeCategory === 'Všetky'
    ? faqs
    : faqs.filter((f) => f.category === activeCategory);

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    setOpenId(null);
  };

  return (
    <section id="faq" className="py-28 bg-zinc-50">
      <div className="max-w-7xl mx-auto px-6">

        {/* Section header */}
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-chat-3-line text-orange-500 text-sm" />
            <span className="text-orange-600 text-sm font-semibold">Časté otázky</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-zinc-900 mb-4 leading-tight">
            Máte{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">
              otázky?
            </span>
            {' '}Máme odpovede.
          </h2>
          <p className="text-zinc-500 text-lg max-w-xl mx-auto leading-relaxed">
            Zozbierali sme najčastejšie otázky od klientov, aby ste vedeli čo čakať ešte pred prvým hovorom.
          </p>
        </div>

        {/* Main layout */}
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">

          {/* Left sticky panel */}
          <div
            className={`lg:w-72 xl:w-80 flex-shrink-0 lg:sticky lg:top-28 transition-all duration-700 delay-100 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
          >
            {/* Stats card */}
            <div className="bg-white border border-zinc-100 rounded-2xl p-6 mb-5">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-xl">
                  <i className="ri-lightbulb-flash-line text-white text-lg" />
                </div>
                <div>
                  <p className="text-zinc-900 font-black text-lg leading-none">{faqs.length} odpovedí</p>
                  <p className="text-zinc-400 text-xs">na vaše otázky</p>
                </div>
              </div>
              <div className="space-y-2.5">
                {faqCategories.filter(c => c !== 'Všetky').map((cat) => {
                  const count = faqs.filter(f => f.category === cat).length;
                  const catColors: Record<string, string> = {
                    Spolupráca: 'bg-orange-400',
                    Ceny: 'bg-emerald-400',
                    Výsledky: 'bg-amber-400',
                    Proces: 'bg-violet-400',
                    Reporting: 'bg-sky-400',
                    Automatizácie: 'bg-rose-400',
                  };
                  return (
                    <button
                      key={cat}
                      onClick={() => handleCategoryChange(cat)}
                      className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm transition-all duration-200 cursor-pointer
                        ${activeCategory === cat ? 'bg-zinc-900 text-white' : 'bg-zinc-50 text-zinc-600 hover:bg-zinc-100'}`}
                    >
                      <div className="flex items-center gap-2.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${catColors[cat] ?? 'bg-zinc-400'}`} />
                        <span className="font-medium">{cat}</span>
                      </div>
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded-md ${activeCategory === cat ? 'bg-white/20 text-white' : 'bg-zinc-200 text-zinc-500'}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
                <button
                  onClick={() => handleCategoryChange('Všetky')}
                  className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm transition-all duration-200 cursor-pointer
                    ${activeCategory === 'Všetky' ? 'bg-zinc-900 text-white' : 'bg-zinc-50 text-zinc-600 hover:bg-zinc-100'}`}
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-zinc-400" />
                    <span className="font-medium">Všetky otázky</span>
                  </div>
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded-md ${activeCategory === 'Všetky' ? 'bg-white/20 text-white' : 'bg-zinc-200 text-zinc-500'}`}>
                    {faqs.length}
                  </span>
                </button>
              </div>
            </div>

            {/* WhatsApp CTA card */}
            <div
              className="rounded-2xl overflow-hidden relative p-5"
              style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
            >
              <div className="absolute top-0 right-0 w-28 h-28 rounded-full bg-white/10 -translate-y-8 translate-x-8" />
              <div className="absolute bottom-0 left-0 w-20 h-20 rounded-full bg-black/10 translate-y-6 -translate-x-6" />
              <div className="relative z-10">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl mb-4">
                  <i className="ri-whatsapp-line text-white text-xl" />
                </div>
                <p className="text-white font-black text-base leading-tight mb-1">
                  Nenašli ste odpoveď?
                </p>
                <p className="text-white/80 text-xs mb-4 leading-relaxed">
                  Napíšte nám na WhatsApp a odpovieme do pár minút.
                </p>
                <a
                  href="https://wa.me/421905929167?text=Dobr%C3%BD%20de%C5%88%2C%20m%C3%A1m%20ot%C3%A1zku..."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-emerald-700 text-sm font-bold px-4 py-2.5 rounded-xl cursor-pointer whitespace-nowrap hover:bg-white/90 transition-colors duration-200"
                >
                  <i className="ri-whatsapp-line" />
                  Napísať teraz
                </a>
              </div>
            </div>
          </div>

          {/* Right accordion */}
          <div className="flex-1 min-w-0">
            {/* Mobile category pills */}
            <div className="flex flex-wrap gap-2 mb-6 lg:hidden">
              {faqCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap
                    ${activeCategory === cat
                      ? 'bg-zinc-900 text-white border-zinc-900'
                      : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'}`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Results count */}
            <div className={`flex items-center justify-between mb-5 transition-all duration-300`}>
              <p className="text-zinc-400 text-sm">
                Zobrazujem{' '}
                <span className="text-zinc-900 font-bold">{filtered.length}</span>
                {' '}otázok
                {activeCategory !== 'Všetky' && (
                  <span> v kategórii <span className="text-orange-500 font-semibold">{activeCategory}</span></span>
                )}
              </p>
              {activeCategory !== 'Všetky' && (
                <button
                  onClick={() => handleCategoryChange('Všetky')}
                  className="text-xs text-zinc-400 hover:text-zinc-600 flex items-center gap-1 cursor-pointer transition-colors"
                >
                  <i className="ri-close-line" />
                  Zrušiť filter
                </button>
              )}
            </div>

            {/* Accordion list */}
            <div className="space-y-3">
              {filtered.map((faq, i) => (
                <AccordionItem
                  key={faq.id}
                  question={faq.question}
                  answer={faq.answer}
                  category={faq.category}
                  isOpen={openId === faq.id}
                  onToggle={() => setOpenId(openId === faq.id ? null : faq.id)}
                  index={i}
                  inView={inView}
                />
              ))}
            </div>

            {/* Bottom note */}
            <div
              className={`mt-10 flex flex-col sm:flex-row items-center gap-4 p-6 bg-white border border-zinc-100 rounded-2xl transition-all duration-700 delay-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            >
              <div className="w-12 h-12 flex items-center justify-center bg-orange-50 rounded-xl flex-shrink-0">
                <i className="ri-customer-service-2-line text-orange-500 text-xl" />
              </div>
              <div className="text-center sm:text-left">
                <p className="text-zinc-900 font-bold text-base mb-0.5">Stále máte otázky?</p>
                <p className="text-zinc-500 text-sm">Dohodnite si bezplatnú 30-minútovú konzultáciu - bez záväzkov, bez skrytých poplatkov.</p>
              </div>
              <button
                onClick={() => { const el = document.querySelector('#kontakt'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
                className="flex-shrink-0 bg-orange-500 hover:bg-orange-400 text-white text-sm font-bold px-5 py-2.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap sm:ml-auto"
              >
                Konzultácia zadarmo
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
